import type {
  loadOptions as rudderstackLoadOptions,
  identify as rudderstackIdentify,
  alias as rudderstackAlias,
  page as rudderstackPage,
  track as rudderstackTrack,
  group as rudderstackGroup,
  reset as rudderstackReset,
  getAnonymousId as rudderstackGetAnonymousId,
  setAnonymousId as rudderstackSetAnonymousId,
  anonymousIdOptions,
} from 'rudder-sdk-js';
import { EventQueue } from './event-queue';

declare global {
  // eslint-disable-next-line no-var
  var rudderanalytics: WindowRudderstack;
}

function isBrowser() {
  return typeof window !== 'undefined';
}

function isEmpty(str: string) {
  return !str;
}

type DebugFn = ((ctx: unknown) => void) | undefined;

export type Method =
  | 'identify'
  | 'alias'
  | 'page'
  | 'track'
  | 'group'
  | 'reset'
  | 'getAnonymousId'
  | 'setAnonymousId';

export type AnalyticsConfig = {
  rudderstack: {
    writeKey: string;
    dataPlaneUrl: string;
    configUrl: string;
    cdnUrl: string;
    loadOptions?: LoadOptions;
    onReady?: () => void;
  };
};

type LoadOptions = Exclude<rudderstackLoadOptions, 'configUrl'>;
type RudderstackSDK = typeof import('rudder-sdk-js');
type LoadedRudderstackSDK = RudderstackSDK & {
  initialized: boolean;
};

export type WindowRudderstack = LoadedRudderstackSDK | undefined;

function isRudderstackInitialized() {
  return isBrowser() && window?.rudderanalytics?.initialized === true;
}

function sendEvent(method: Method, args: unknown[]) {
  // @ts-expect-error - The error is expected since TypeScript cannot combine all of the method types with all of the different method signatures
  // For similar scenarios, see https://github.com/microsoft/TypeScript/issues/33591
  window.rudderanalytics[method](...args);
}

const events = new EventQueue();

function sendOrQueueEvent(method: Method, args: unknown[]) {
  if (isRudderstackInitialized()) {
    sendEvent(method, args);
  } else {
    events.enqueue(method, args);
  }
}

/**
 * @throws Error
 */

export async function init(
  cfg: AnalyticsConfig,
  debug?: DebugFn,
): Promise<void> {
  if (isBrowser()) {
    if (isRudderstackInitialized()) {
      return;
    }

    const rudderstackCfg = cfg.rudderstack;

    if (isEmpty(rudderstackCfg.writeKey)) {
      throw new Error('@ha/analytics: missing writeKey');
    }

    if (isEmpty(rudderstackCfg.dataPlaneUrl)) {
      throw new Error('@ha/analytics: missing dataPlaneUrl');
    }

    if (isEmpty(rudderstackCfg.configUrl)) {
      throw new Error('@ha/analytics: missing configUrl');
    }

    return import('rudder-sdk-js').then((sdk: RudderstackSDK) => {
      window.rudderanalytics = sdk as LoadedRudderstackSDK;

      const integrations = {
        All: true,
        ...rudderstackCfg.loadOptions?.integrations,
      };
      const loadOptions: rudderstackLoadOptions = {
        ...rudderstackCfg.loadOptions,
        ...integrations,
        configUrl: rudderstackCfg.configUrl,
        destSDKBaseURL: rudderstackCfg.cdnUrl + '/v1.1/js-integrations',
      };

      debug?.({ message: 'Rudderstack load options', loadOptions });

      window.rudderanalytics.load(
        rudderstackCfg.writeKey,
        rudderstackCfg.dataPlaneUrl,
        loadOptions,
      );

      debug?.({ message: 'Rudderstack loaded' });

      window.rudderanalytics.ready(() => {
        debug?.({ message: 'Rudderstack ready' });
        rudderstackCfg.onReady?.();

        events.batchDequeue(event => {
          const [method, ...args] = event;

          sendEvent(method, args);
        });
      });
    });
  } else {
    return Promise.reject(
      new Error('@ha/analytics: not supported outside browser'),
    );
  }
}

export const identify: typeof rudderstackIdentify = (...args: unknown[]) => {
  sendOrQueueEvent('identify', args);
};

export const alias: typeof rudderstackAlias = (...args: unknown[]) => {
  sendOrQueueEvent('alias', args);
};

export const page: typeof rudderstackPage = (...args: unknown[]) => {
  sendOrQueueEvent('page', args);
};

export const track: typeof rudderstackTrack = (...args: unknown[]) => {
  sendOrQueueEvent('track', args);
};

export const group: typeof rudderstackGroup = (...args: unknown[]) => {
  sendOrQueueEvent('group', args);
};

export const reset: typeof rudderstackReset = (...args: unknown[]) => {
  sendOrQueueEvent('reset', args);
};

export const getAnonymousId: typeof rudderstackGetAnonymousId = (
  options?: anonymousIdOptions,
) => {
  if (isRudderstackInitialized() && window.rudderanalytics) {
    return window.rudderanalytics.getAnonymousId(options);
  }

  return '';
};

export const setAnonymousId: typeof rudderstackSetAnonymousId = (
  ...args: unknown[]
) => {
  sendOrQueueEvent('setAnonymousId', args);
};
